import { api, type RequestOptions } from './client';

/**
 * Typed captive-portal API client — a 1:1 mirror of the FROZEN wave-3b contract
 * (apps/admin/src/shared/api/network/captive.ts), which itself mirrors the
 * backend wire structs in internal/captive/captive.go EXACTLY. The types are
 * copied verbatim (not re-derived) so this self-contained subscriber app consumes
 * the identical contract without importing across the admin app's tsconfig
 * boundary. If the backend struct changes, update it there first, then here.
 *
 * ONE DELIBERATE DEVIATION (D23): the `source_ip`/`vlan` fields of the context and
 * register inputs are OPTIONAL here. They always were on the wire (Go decodes a
 * missing key to the zero value, which both handlers treat as "derive it from the
 * connection"); typing them as required is what made the SPA unusable behind a
 * plain DNAT redirect, which cannot append `?ip=`/`?vlan=`.
 *
 * AUTH: `/context` and `/login` are unauthenticated. `/devices`,
 * `/static-addresses`, `/register`, `/deregister`, `/account`, `/activate`,
 * `/topup` require the login token in an `Authorization: Bearer <token>` header
 * (NOT a session cookie) — see `bearer()`.
 *
 * Routes (mounted at /api/v1/captive):
 *   POST /captive/context  · POST /captive/login
 *   GET  /captive/devices  · POST /captive/register · POST /captive/deregister
 *   GET  /captive/account  · POST /captive/activate · POST /captive/topup
 *   GET  /captive/static-addresses
 */

/** {items,total} list envelope — the core's standard list shape. */
export interface ListEnvelope<T> {
  items: T[];
  total: number;
}

/**
 * Account state returned by the portal — the ONLY routing key the SPA branches on.
 *
 * `blocked` is GONE (it was split into `financial_block` / `admin_block` /
 * `disabled` when self-activation landed): a never-activated account is
 * `awaiting_activation`, an unfunded one `financial_block`, an operator-blocked one
 * `admin_block`, and an account switched off after activation `disabled`.
 */
export type CaptiveState =
  | 'serviceable'
  | 'awaiting_activation'
  | 'financial_block'
  | 'admin_block'
  | 'disabled'
  /**
   * DEGRADED, not a state of the account: the core could not establish it (its
   * account read failed, or no checker is installed). It exists so the core never
   * has to invent a state — inventing one used to report an admin-blocked
   * subscriber as `serviceable`. The SPA renders a retry screen for it and must
   * never treat it as service.
   */
  | 'unknown';

/** The screen the core suggests for a state (advisory; the SPA routes on `state`). */
export type CaptiveNext = 'register' | 'activation' | 'replenishment' | 'support' | 'retry';

/**
 * The account-state block shared verbatim by `POST /captive/login` (plus `token`),
 * `GET /captive/account` and `POST /captive/activate` (plus `activated`).
 *
 * EVERY money field is a DECIMAL STRING (`money.Amount`, e.g. "1000.00") — never
 * parse it into a float; format it with `formatRubles()` / compare it with
 * `isPositiveMoney()` from portal/format.ts.
 */
export interface CaptiveAccountState {
  account_id: number;
  state: CaptiveState;
  next?: CaptiveNext;
  /** Contract balance. */
  balance: string;
  /** balance + credit limit + active promise — what charging is gated on. */
  availability: string;
  /** Resolved minimum balance to activate (account → groups MAX → global). */
  min_balance: string;
  /** What activating will debit right now; "0.00" when unknown/not chargeable. */
  activation_charge: string;
  /** max(min_balance, activation_charge). */
  required: string;
  /** max(0, required − availability); "0.00" when the account can pay. */
  shortfall: string;
  can_activate: boolean;
  tariff_name?: string;
  /**
   * THE TARIFF-GROUP ROUTING KEY (2026-08-16). True when the account has NO primary
   * tariff and activates on a plan the SUBSCRIBER picks out of its activation tariff
   * group — the activation screen must offer the plan list before it can offer a
   * button, and `can_activate` is false until one is chosen.
   *
   * It rides on this block, which every poll already fetches, so the screen never has
   * to pay for the (per-plan-quote) options call just to learn which view to draw.
   */
  tariff_choice_required: boolean;
  /** The group's name, for the heading above the plans. */
  tariff_group_name?: string;
}

/**
 * One plan on the activation choice screen (`activationOptionWire`).
 *
 * The two figures are the customer's own requirement, and they answer different
 * questions: `subscription_fee` is what the plan costs per period ("600,00 ₽ в месяц")
 * and `activation_charge` is what pressing the button debits RIGHT NOW — the prorated
 * remainder of the current period, priced through the same pack the debit posts a
 * moment later. Showing only one of them would misquote the subscriber.
 *
 * Every money field is a DECIMAL STRING, like the rest of this contract.
 */
export interface CaptiveActivationOption {
  tariff_id: number;
  name: string;
  description: string;
  /** 'A' | 'B' | 'C' — how the plan is charged; the screen states it in words. */
  billing_model: string;
  /** The plan's base rate in kbit/s; absent when the tariff sets none. */
  bandwidth_kbps?: number;
  /** FULL price of a period. */
  subscription_fee: string;
  /** What starting THIS plan debits now. Meaningless unless `charge_known`. */
  activation_charge: string;
  /**
   * False = nobody could price this plan. The screen must SAY so rather than render
   * "0,00 ₽", and `can_activate` is false.
   */
  charge_known: boolean;
  /** max(min_balance, this plan's charge). */
  required: string;
  /** What must be PAID IN to activate on this plan. */
  shortfall: string;
  can_activate: boolean;
}

/** `GET /captive/activation-options` result. */
export interface CaptiveActivationOptions {
  account_id: number;
  /**
   * The account has no primary tariff, so activation cannot proceed until a plan is
   * chosen. TRUE with an empty `options` is a real state and a MISCONFIGURATION — the
   * screen must say so rather than present an empty list as if it were normal.
   */
  choice_required: boolean;
  group_name?: string;
  /** The group is archived, which is why `options` is empty. */
  group_archived: boolean;
  min_balance: string;
  balance: string;
  availability: string;
  /** False = the money read failed; plans are still listed, every verdict is false. */
  funds_known: boolean;
  options: CaptiveActivationOption[];
}

/**
 * Both connection fields are OPTIONAL on the wire (Go zero values): omitted, the
 * core resolves the address from the request connection and does an address-only
 * lease lookup. The portal sends them only when the URL supplied an override — see
 * portal/connection.ts (D23).
 */
export interface CaptiveContextInput {
  /**
   * Optional cross-check only: the core reads the lease of the CALLER's own address
   * and refuses a body value naming a different one with 403
   * `captive.source_ip_mismatch`. Omitted ⇒ the connection address is used.
   */
  source_ip?: string;
  /**
   * Accepted and IGNORED by the core: a lease is keyed (address, VLAN), so a
   * caller-named VLAN was a key into somebody else's lease. Still sent when the
   * redirect supplies `?vlan=` so an older core keeps working.
   */
  vlan?: number;
}

/**
 * Captive lease identity context (contextResult).
 *
 * ⚠ `POST /captive/context` NO LONGER 404s WHEN THERE IS NO LEASE *(2026-08-22, the
 * customer's request)*. It answers `200` carrying `client_ip` with `mac: ''`, so a
 * resolved context is no longer evidence that the device was identified — `mac` is.
 * Any caller branching on «did the context read succeed?» must branch on `context.mac`
 * instead, which is what `identityUnavailable` in EnrollmentFlow.tsx does.
 */
export interface CaptiveContext {
  /**
   * THE ADDRESS THE CORE SAW THIS REQUEST ARRIVE FROM — the point of the whole change
   * (customer, 2026-08-22: «show user's IP at the reg.centrlan.net front page, before
   * any login is entered, even if user's port is not detected, so operator can ask
   * user to open this page and tell this IP»).
   *
   * It is always the SERVER's resolution and never anything this app sent: a body
   * `source_ip` naming a different address is refused with 403
   * `captive.source_ip_mismatch` before any address is emitted. Present on every 200,
   * including the no-lease one.
   *
   * ⚠ Behind a subscriber's own home router this is the CPE's WAN address, not what
   * his laptop believes its address is — that is still the address billing holds and
   * therefore the right one to read out, and the screen says so (`connection.ipNote`).
   */
  client_ip: string;
  /** '' when no captive lease backs the address — the device is NOT identified. */
  mac: string;
  opt82_circuit?: string;
  opt82_remote?: string;
  trap_switch?: string;
  trap_port?: number;
  vlan: number;
}

export interface CaptiveLoginInput {
  login: string;
  password: string;
}

/** Login result (loginResult): the session token + the account-state block. */
export interface CaptiveLoginResult extends CaptiveAccountState {
  token: string;
}

/** `GET /captive/account` result: the §3.1 body minus the token (re-poll after a top-up). */
export type CaptiveAccountResult = CaptiveAccountState;

/**
 * `POST /captive/activate` 200 result: the state block, now serviceable.
 *
 * The core ALSO mints a refreshed `token` here (the frozen contract did not mention
 * it): the token carries a `blocked` claim derived from the state, and the one from
 * before the activation still says blocked, which would keep the rest of the session
 * on the non-serviceable path. Callers must adopt it when present.
 */
export interface CaptiveActivateResult extends CaptiveAccountState {
  activated: boolean;
  token?: string;
}

/**
 * `.details` of the `captive.insufficient_balance` (409) error thrown by `activate`
 * — the live figures at the moment the activation was refused, which the activation
 * screen renders instead of a generic failure.
 */
export interface CaptiveInsufficientBalanceDetails {
  balance: string;
  required: string;
  shortfall: string;
  min_balance: string;
  activation_charge: string;
}

/** `POST /captive/topup` body. `amount` is a decimal string. */
export interface CaptiveTopUpInput {
  amount: string;
  save_card: boolean;
}

/**
 * `POST /captive/topup` success shape — frozen now, served later: until PayOnline
 * is installed (Phase 3) the endpoint answers `503 captive.topup_unavailable`, and
 * the portal shows the office/ОСМП notice instead of an error. The shape is typed
 * today so wiring the provider changes no caller: redirect to `redirect_url`.
 */
export interface CaptiveTopUpIntent {
  provider: string;
  payment_id: string;
  redirect_url: string;
}

/** A subscriber-facing device projection returned by the portal (deviceWire). */
export interface CaptiveDevice {
  id: number;
  mac: string;
  identity_form: string;
  opt82_circuit?: string | null;
  opt82_remote?: string | null;
  trap_switch_id?: number | null;
  trap_port?: number | null;
  origin: string;
  registered_at: string;
  last_online_at?: string | null;
}

/**
 * One static address the subscriber's own account holds and no device is using — a
 * row of `GET /captive/static-addresses` (customer request 2026-08-17; this half
 * 2026-08-18). Mirrors `staticAddressWire` in internal/captive/static_addresses.go.
 *
 * ⚠ TWO FIELDS, AND THAT IS THE DESIGN, NOT AN ABBREVIATION. The operator's own row
 * carries `pool_id`, `pool_name`, `state`, `logout_at` and `pool_dhcp_issuance` as
 * well; every one of them says something about the operator's network, and this
 * response is served on the walled-garden listener, which any device holding a
 * captive address can reach. A third field appearing here would mean a deliberate
 * server-side decision — both the Go key-set test and the OpenAPI pin refuse one.
 */
export interface CaptiveStaticAddress {
  /** Send this back as `static_lease_id` on `register`. */
  lease_id: number;
  address: string;
}

export interface CaptiveRegisterInput {
  /**
   * Optional cross-check only: the core binds the registration to the CALLER's own
   * address and refuses a different one with 403 `captive.source_ip_mismatch`.
   * Omitted ⇒ the connection address is used.
   */
  source_ip?: string;
  /** Accepted and IGNORED by the core (see CaptiveContextInput.vlan). */
  vlan?: number;
  replace_id?: number | null;
  /**
   * The static address the subscriber picked for this device (customer request,
   * 2026-08-17). Mirrors `registerReq.StaticLeaseID` in internal/captive/captive.go,
   * which landed the same day; bound to the newly created device atomically with it,
   * inside the registration's own transaction (`devices.bindChosenStatic`).
   *
   * ⚠ THE VALUE MAY ONLY EVER BE A `lease_id` FROM `captiveApi.staticAddresses()`,
   * which lists the token account's OWN assigned-but-unbound statics. Never a guessed
   * id, never one from a staff route, and never from
   * `/accounts/{id}/static-addresses/candidates` — that one lists addresses still in
   * the POOL, i.e. NOBODY's, an operator's concern that must not be reachable from the
   * walled garden at all. Omitting the key is what makes a registration DYNAMIC (the
   * Go pointer decodes to nil), and that is the default.
   *
   * ~~⚠ NOTHING IN THIS APP SETS IT, AND THAT IS A FINDING, NOT AN OVERSIGHT … the
   * portal has no authorised way to offer the choice today … a subscriber-scoped
   * listing route is what is missing; until it exists this field stays typed and
   * unset.~~ STRUCK IN PLACE 2026-08-18: the missing route was built
   * (`GET /captive/static-addresses` → `captiveApi.staticAddresses`), and
   * `portal/AddressChoiceScreen.tsx` offers the choice. It is struck rather than
   * deleted because its ARGUMENT still governs every future edit here — the ids come
   * from that route and from nowhere else, and a selector fed by anything else would
   * be either a staff call made from the walled garden or an invented id, which is
   * the D23 shape.
   *
   * When it is sent and the core refuses it, the refusal carries NO `details` on this
   * listener (D330, `internal/captive.writeDevErr`) — the details would name another
   * subscriber's account. See `registerErrorMessage` in portal/EnrollmentFlow.tsx for
   * the codes and their copy.
   */
  static_lease_id?: number | null;
}

/**
 * WHAT `POST /captive/register` ACTUALLY DID (`outcome`, landed 2026-09-01 with the
 * backend half of D408).
 *
 * ⚠ SINCE D408, «THE REQUEST SUCCEEDED» AND «A DEVICE IS BOUND» ARE NO LONGER THE
 * SAME FACT. The core used to answer `404 captive.context_not_found` for any address
 * with no live captive lease, which is the state every financially suspended
 * subscriber is in (he sits in the walled garden holding his ordinary PUBLIC
 * address). It now answers `200` with the account-state block for ANY address inside
 * a pool this ISP declared, having written nothing — so a 200 alone no longer
 * licenses a screen that says «устройство зарегистрировано … доступ откроется».
 * The core states which of the three things it did rather than leaving the SPA to
 * infer it from `device` being absent, and its own constant comment hands the
 * obligation over: «whether that state is one he can act on is the front-end's
 * decision, and "доступ откроется" is not an answer it may give here.»
 */
export type CaptiveRegisterOutcome =
  /**
   * A device was bound or re-bound BY THIS CALL, from the identity of a live captive
   * lease. The only value that licenses the confirmation screen's device claim.
   */
  | 'registered'
  /**
   * Nothing was written because the binding was ALREADY valid — the calling MAC is
   * one of the account's active devices. ⚠ A VALID BINDING, not a failure: the
   * subscriber is routed on by his account state exactly as if this call had bound
   * him, and `device` is absent only because there was nothing new to report.
   */
  | 'already_bound'
  /**
   * The caller is on an address this ISP owns, but no live captive lease backs it,
   * so billing holds no device identity and nothing was registered. His account
   * state is still reported — that IS the D408 repair — but no screen may promise
   * that access is opening on the strength of this call.
   */
  | 'no_device_identity';

/**
 * Register result (registerResult): the bound device (absent when there was nothing
 * to register) PLUS the whole account-state block, so the portal can route a
 * non-serviceable subscriber to activation / replenishment / support without a
 * second call — and with figures one round trip fresher than the login body's.
 */
export interface CaptiveRegisterResult extends CaptiveAccountState {
  device?: CaptiveDevice;
  /**
   * ⚠ TYPED `string`, NOT `CaptiveRegisterOutcome`, AND OPTIONAL — DELIBERATELY.
   * The core ships it on every 200 with no `omitempty`, so in practice it is always
   * there; the width is here so that neither of the two ways this field can arrive
   * unrecognised can be read as «registered». An OLDER core (a rollback, a NAS-side
   * deployment that lagged the SPA) sends no field at all, and a LATER one may add a
   * fourth value — and a union type would tempt an exhaustive `switch` whose
   * `default` is the branch that promises a subscriber access he does not have.
   * `registrationIsBound` below is the only reader, and it fails towards «not bound»,
   * which is the safe direction the server's own constant block names.
   */
  outcome?: string;
}

/**
 * Does this register result mean the account HAS a valid binding for the calling
 * device? True for `registered` and `already_bound`; false for `no_device_identity`,
 * for a missing field and for anything unrecognised.
 *
 * It lives beside the wire type rather than in the flow because it IS the wire
 * contract's safe-direction rule, and a second reader that spelled the two literals
 * itself would be one edit away from disagreeing with this one.
 */
export function registrationIsBound(outcome: string | undefined): boolean {
  return outcome === 'registered' || outcome === 'already_bound';
}

/**
 * The `.details` payload of the `captive.device_limit_reached` (409) error thrown
 * by `register` when the account's device limit (>1) is fully used: the current
 * bindings, one of which the subscriber must pick to de-register.
 */
export interface CaptiveAtLimitDetails {
  limit: number;
  devices: CaptiveDevice[];
}

/** The subscriber Bearer token, attached to the authenticated portal calls. */
export interface CaptiveAuth {
  token: string;
}

const bearer = (auth: CaptiveAuth): RequestOptions => ({
  headers: { Authorization: `Bearer ${auth.token}` },
});

export const captiveApi = {
  /** Read the captive lease identity context for a source IP (unauthenticated). */
  context: (input: CaptiveContextInput) => api.post<CaptiveContext>('/captive/context', input),
  /** Validate billing credentials → session token + account state (unauthenticated). */
  login: (input: CaptiveLoginInput) => api.post<CaptiveLoginResult>('/captive/login', input),
  /** The token account's registered devices — the de-register choice list. */
  devices: (auth: CaptiveAuth) =>
    api.get<ListEnvelope<CaptiveDevice>>('/captive/devices', bearer(auth)),
  /**
   * The token account's OWN static addresses that are on no device — the only ids
   * `register` accepts as `static_lease_id`.
   *
   * An empty list is the ordinary answer (almost every subscriber holds none) and
   * means «render no control». A `503 captive.not_wired` is a core with no address
   * reader installed; the portal presents that to the SUBSCRIBER the same way, because
   * there is nothing he can do about it and no choice to offer him — while the core
   * keeps the two distinguishable on purpose, so an OPERATOR can tell a lost wiring
   * from an estate where nobody has a static.
   */
  staticAddresses: (auth: CaptiveAuth) =>
    api.get<ListEnvelope<CaptiveStaticAddress>>('/captive/static-addresses', bearer(auth)),
  /** Idempotent register / replace (device-limit rule). Throws on 409 at-limit. */
  register: (input: CaptiveRegisterInput, auth: CaptiveAuth) =>
    api.post<CaptiveRegisterResult>('/captive/register', input, bearer(auth)),
  /** Explicit de-register of one device (Personal-Area / choice parity). */
  deregister: (deviceId: number, auth: CaptiveAuth) =>
    api.post<void>('/captive/deregister', { device_id: deviceId }, bearer(auth)),
  /**
   * Re-read the account state with the token already in hand — used to pick up an
   * office/ОСМП payment without asking for the password again.
   */
  account: (auth: CaptiveAuth) =>
    api.get<CaptiveAccountResult>('/captive/account', bearer(auth)),
  /**
   * Self-activate a never-activated account. Throws `captive.insufficient_balance`
   * (409, `.details` = CaptiveInsufficientBalanceDetails),
   * `captive.activation_not_available` (409), `captive.tariff_not_selectable` (409 —
   * the picked plan was withdrawn meanwhile) or `captive.tariff_choice_required` (409 —
   * a group account sent no plan).
   */
  activate: (auth: CaptiveAuth, tariffID?: number) =>
    api.post<CaptiveActivateResult>(
      '/captive/activate',
      // The chosen plan, when the account activates out of a tariff group. Omitted
      // entirely otherwise, which is byte-for-byte the pre-2026-08-16 request.
      tariffID == null ? {} : { tariff_id: tariffID },
      bearer(auth),
    ),
  /**
   * The plans this account may activate on, each priced for it. A separate call from
   * `account` because it costs one activation quote per candidate; fetch it when the
   * choice screen opens and after a top-up lands, not on every poll.
   */
  activationOptions: (auth: CaptiveAuth) =>
    api.get<CaptiveActivationOptions>('/captive/activation-options', bearer(auth)),
  /**
   * Start a card top-up. Answers `503 captive.topup_unavailable` until the payment
   * provider is installed (Phase 3) — callers must present that calmly, not as an
   * error, and point at the office/ОСМП route instead.
   */
  topup: (input: CaptiveTopUpInput, auth: CaptiveAuth) =>
    api.post<CaptiveTopUpIntent>('/captive/topup', input, bearer(auth)),
};
